import { TDocumentDefinitions } from "pdfmake/interfaces";
import moment from "moment";

import CompanyLogoForm from "react-lib/apps/HISV3/common/CompanyLogoForm";

export type FormMeLabResultProps = {
  items: {
    name: string;
    name_en: string;
    value: string;
    unit: string;
    refValue: string;
    flag: string;
    isDivisionHeader?: boolean;
    isParentHeader?: boolean;
    isChild?: boolean;
  }[];
  patient: Record<string, any>;
  labNo: string;
  collectedDatetime: string;
  receivedDatetime: string;
  reportedDatetime: string;
  reportedBy: string;
  reportedByLicence: string;
  reportedDate: string;
  reportedTime: string;
  authorizedBy: string;
  authorizedByLicence: string;
  authorizedDate: string;
  authorizedTime: string;
  isConfidential: boolean;
  printedBy?: string;
  logoImage?: string;
  reportPdfLink?: string;
  remark?: string;
};

const COLOR = {
  black: "#000000",
  bannerBg: "#646668",
  flag: "#FF0000",
  confidential: "#D02149",
};

const FS = {
  lg: 18, // companyName, bannerTitle, confidentialTitle
  md: 14, // label, value, tableHeader, parentHeader
  hmd: 13, // tableCell
  sm: 12, // address, confidentialSub
  xs: 10, // disclaimer, printDate
  divisionHeader: 15,
  reporter: 13,
};

const NB: [boolean, boolean, boolean, boolean] = [false, false, false, false];
const TB: [boolean, boolean, boolean, boolean] = [false, true, false, true];
const BC = [COLOR.black, COLOR.black, COLOR.black, COLOR.black];

const HEADER_HEIGHT = 210;
const FOOTER_HEIGHT_NORMAL = 155;
const FOOTER_HEIGHT_CONFIDENTIAL = 180;

const fmtDate = (v: string) => {
  if (!v) return "";
  const m = moment(v, "DD/MM/YYYY", true);
  return m.isValid() ? m.format("DD MMM YYYY") : v;
};

const fmtDatetime = (v: string) => {
  if (!v) return "";
  const m = moment(v);
  return m.isValid() ? m.format("DD MMM YYYY  HH:mm") : v;
};

const reporterRow = (label: string, name: string, code: string, date: string, time: string) => [
  { text: label, fontSize: FS.reporter, border: NB, lineheight: 0.9 },
  {
    text: [name || "", code || ""].filter(Boolean).join("  "),
    fontSize: FS.reporter,
    border: NB,
    lineheight: 0.9,
  },
  { text: date || "", fontSize: FS.reporter, border: NB, lineheight: 0.9 },
  { text: time || "", fontSize: FS.reporter, border: NB, lineheight: 0.9 },
];

const FormMeLabResult = async (props: FormMeLabResultProps): Promise<TDocumentDefinitions> => {
  console.debug("debug ~ FormMeLabResult ~ props:", props);
  const { font, images, logoWidth } = await CompanyLogoForm({
    font: "THSarabunNew",
    form: "FormMeLabResult",
    height: 70,
    logoImage: props.logoImage,
  });

  const patient = props.patient || {};
  const gender =
    patient.gender === "M" ? "Male" : patient.gender === "F" ? "Female" : patient.gender_name || "";

  // ============ Lab results table ============
  const tableBody: any[][] = [
    // Header row with top+bottom border
    [
      { text: "Test Name", bold: true, fontSize: FS.divisionHeader, border: TB, borderColor: BC },
      { text: "Result", bold: true, fontSize: FS.divisionHeader, border: TB, borderColor: BC },
      { text: "Flag", bold: true, fontSize: FS.divisionHeader, border: TB, borderColor: BC },
      { text: "Unit", bold: true, fontSize: FS.divisionHeader, border: TB, borderColor: BC },
      {
        text: "Reference Range",
        bold: true,
        fontSize: FS.divisionHeader,
        border: TB,
        borderColor: BC,
      },
    ],
  ];

  for (const item of props.items) {
    if (item.isDivisionHeader) {
      tableBody.push([
        {
          text: item.name,
          bold: true,
          decoration: "underline",
          fontSize: FS.divisionHeader,
          colSpan: 5,
          border: NB,
          marginTop: 4,
        },
        {},
        {},
        {},
        {},
      ]);
    } else if (item.isParentHeader) {
      tableBody.push([
        {
          text: item.name,
          bold: true,
          fontSize: FS.hmd,
          colSpan: 5,
          border: NB,
          marginTop: 1,
        },
        {},
        {},
        {},
        {},
      ]);
    } else {
      let primaryName = item.name_en || "";
      let secondaryName = item.name || "";

      primaryName = primaryName.trim();
      secondaryName = secondaryName.trim();

      if (!secondaryName && primaryName) {
        const parts = primaryName
          .split(/\s{2,}|\t/)
          .map((p) => p.trim())
          .filter(Boolean);
        if (parts.length > 1) {
          primaryName = parts[0];
          secondaryName = parts[1];
        }
      }

      const nameCell =
        primaryName && secondaryName && primaryName !== secondaryName
          ? {
              columns: [
                { text: primaryName, width: 120, fontSize: FS.hmd },
                { text: secondaryName, width: "*", fontSize: FS.hmd },
              ],
              marginLeft: item.isChild ? 8 : 0,
              border: NB,
            }
          : {
              text: primaryName || secondaryName,
              fontSize: FS.hmd,
              marginLeft: item.isChild ? 8 : 0,
              border: NB,
            };

      tableBody.push([
        nameCell,
        { text: item.value || "", fontSize: FS.hmd, border: NB },
        {
          text: item.flag || "",
          fontSize: FS.hmd,
          color: item.flag ? COLOR.flag : COLOR.black,
          border: NB,
          bold: true,
        },
        { text: item.unit || "", fontSize: FS.hmd, border: NB },
        {
          text: item.refValue || "",
          fontSize: FS.hmd,
          border: NB,
        },
      ]);
    }
  }

  // ============ Footer (repeats every page) ============
  const printMoment = moment();
  const printDate = printMoment.format("DD MMM YYYY");
  const printTime = printMoment.format("HH:mm");

  const footer = (): any => {
    const stack: any[] = [];

    if (props.isConfidential) {
      stack.push({
        text: [
          {
            text: "PRIVATE & CONFIDENTIAL ",
            bold: true,
            fontSize: FS.lg,
            color: COLOR.confidential,
          },
          {
            text: "(Authorized Personnel and Data Subject Only)",
            fontSize: FS.sm,
            color: COLOR.black,
          },
        ],
        marginBottom: 4,
      });
    }

    stack.push({
      table: {
        widths: ["auto", "*"],
        body: [
          [
            {
              text: "Remark :",
              fontSize: FS.reporter,
              lineHeight: 0.9,
              border: [false, true, false, false],
              borderColor: BC,
            },
            {
              text: props.remark || "",
              fontSize: FS.reporter,
              lineHeight: 0.9,
              border: [false, true, false, false],
              borderColor: BC,
            },
          ],
        ],
      },
      layout: {
        paddingTop: () => 1,
        paddingBottom: () => 1,
        paddingLeft: () => 0,
        paddingRight: () => 0,
      },
    });

    // Reporter/Authorizer lines wrapped in a single table
    if (props.reportedBy || props.authorizedBy) {
      const tableBodyData: any[][] = [
        [
          ...reporterRow(
            "Reported by :",
            props.reportedBy,
            props.reportedByLicence,
            props.reportedDate,
            props.reportedTime ? `[${props.reportedTime}]` : "",
          ),
        ],
      ];

      if (props.authorizedBy) {
        tableBodyData.push([
          ...reporterRow(
            "Authorised by :",
            props.authorizedBy,
            props.authorizedByLicence,
            props.authorizedDate,
            props.authorizedTime ? `[${props.authorizedTime}]` : "",
          ),
        ]);
      }

      tableBodyData.push([
        ...reporterRow(
          "Printed by :",
          props.printedBy || "",
          "",
          printDate,
          printTime ? `[${printTime}]` : "",
        ),
      ]);

      const tableObj: any = {
        table: {
          // กว้างตามเนื้อหาทุกคอลัมน์ เหลือ "*" ท้ายสุดไว้ทิ้งช่องว่าง สองบล็อกจะได้ไม่ถูกดันไปชิดขวา
          widths: ["auto", "auto", "auto", "*"],
          body: tableBodyData,
        },
        layout: {
          hLineWidth: (i: number, node: any) => (i === 0 || i === node.table.body.length ? 1 : 0),
          vLineWidth: () => 0,
          hLineColor: () => COLOR.black,
          fillColor: () => null,
          paddingTop: () => 1,
          paddingBottom: () => 1,
          paddingLeft: () => 0,
          paddingRight: (i: number) => (i === 7 ? 0 : i === 3 ? 10 : 4),
        },
      };

      const BT: [boolean, boolean, boolean, boolean] = [false, true, false, false];
      const BB: [boolean, boolean, boolean, boolean] = [false, false, false, true];
      const TB: [boolean, boolean, boolean, boolean] = [false, true, false, true];

      if (tableObj.table.body.length > 1) {
        tableObj.table.body[0].forEach((cell: any) => {
          cell.border = BT;
          cell.borderColor = BC;
        });
        tableObj.table.body[tableObj.table.body.length - 1].forEach((cell: any) => {
          cell.border = BB;
          cell.borderColor = BC;
        });
      } else {
        tableObj.table.body[0].forEach((cell: any) => {
          cell.border = TB;
          cell.borderColor = BC;
        });
      }

      stack.push(tableObj);
    }

    // Disclaimers + Print Date + QR
    stack.push({
      columns: [
        {
          width: "*",
          stack: [
            {
              text: "\u2022 This report has been approved electronically. No signature is required",
              fontSize: FS.xs,
              marginTop: 3,
            },
            {
              text: "\u2022 The analysis is based solely on the specimen received on the above date only",
              fontSize: FS.xs,
            },
            {
              text: [
                { text: "\u2022 " },
                { text: "PROTECTED HEALTH INFORMATION (PHI):", bold: true },
                { text: " Unauthorized disclosure is strictly prohibited" },
              ],
              fontSize: FS.xs,
            },
          ],
          lineHeight: 1.2,
        },
        {
          margin: [5, 5, 5, 15],
          width: 80,
          stack: props.reportPdfLink ? [{ qr: props.reportPdfLink, fit: 75 }] : [{ text: "" }],
        },
      ],
      marginTop: 1,
    });

    return { margin: [20, 0, 20, 0], stack };
  };

  let footerHeight = props.isConfidential ? FOOTER_HEIGHT_CONFIDENTIAL : FOOTER_HEIGHT_NORMAL;
  if (props.authorizedBy) {
    footerHeight += 15;
  }

  // คำนวณจำนวนบรรทัดของ remark ที่เกินจาก 1 บรรทัด (ประมาณ 90-95 ตัวอักษรต่อบรรทัด หรือขึ้นบรรทัดใหม่ด้วย \n)
  const remarkText = (props.remark || "").trim();

  if (remarkText) {
    const CHARS_PER_LINE = 130;
    const remarkLines = remarkText.split("\n").reduce((sum, line) => {
      return sum + Math.max(1, Math.ceil((line.length || 1) / CHARS_PER_LINE));
    }, 0);
    const extraRemarkLines = Math.max(0, remarkLines - 1);
    footerHeight += extraRemarkLines * 13; // เพิ่มบรรทัดละ ~13 pt
  }

  return {
    defaultStyle: { font, fontSize: FS.md, lineHeight: 1.1 },
    images: { ...images },
    pageMargins: [20, HEADER_HEIGHT, 20, footerHeight],
    pageSize: "A4",
    header: () => ({
      margin: [20, 10, 20, 0],
      stack: [
        {
          columns: [
            props.logoImage
              ? { image: "logo", width: logoWidth, height: 70, marginTop: 2 }
              : { text: "", width: 76 },
            {
              table: {
                widths: ["*", "*"],
                body: [
                  [
                    {
                      stack: [
                        {
                          text: "มีเฮลท์ คลินิกเทคนิคการแพทย์",
                          bold: true,
                          fontSize: FS.lg,
                          lineHeight: 1.0,
                        },
                        {
                          text: "261/27-28 ซอยจรัญสนิทวงศ์ 29/1 ถนนจรัญสนิทวงศ์",
                          fontSize: FS.sm,
                          lineHeight: 0.9,
                        },
                        {
                          text: "แขวงบางขุนศรี เขตบางกอกน้อย กรุงเทพฯ 10700",
                          fontSize: FS.sm,
                          lineHeight: 0.9,
                        },
                        {
                          text: "โทร. 08-3838-4657 อีเมล: mehealth@morishealth.com",
                          fontSize: FS.sm,
                          lineHeight: 0.9,
                        },
                      ],
                      alignment: "left",
                    },
                    {
                      stack: [
                        {
                          text: "MeHealth Medical Laboratory",
                          bold: true,
                          fontSize: FS.lg,
                          lineHeight: 1.0,
                        },
                        {
                          text: "261/27-28 Soi Charan Sanit Wong 29/1,",
                          fontSize: FS.sm,
                          lineHeight: 0.9,
                        },
                        {
                          text: "Charan Sanit Wong Road, Bang Khun Si, Bangkok 10700",
                          fontSize: FS.sm,
                          lineHeight: 0.9,
                        },
                        {
                          text: "Tel. 08-3838-4657 Email: mehealth@morishealth.com",
                          fontSize: FS.sm,
                          lineHeight: 0.9,
                        },
                      ],
                      alignment: "left",
                    },
                  ],
                ],
              },
              width: "*",
              marginLeft: 12,
              layout: {
                hLineWidth: () => 0,
                vLineWidth: (i) => (i === 1 ? 2 : 0),
                vLineColor: () => "#E5436E",
                paddingLeft: (i) => (i === 0 ? 17 : 17),
                paddingRight: (i) => 0,
                paddingTop: () => 0,
                paddingBottom: () => 0,
              },
            },
          ],
          marginBottom: 4,
        },
        {
          table: {
            widths: ["*"],
            body: [
              [
                {
                  text: "Laboratory Report / รายงานผลตรวจทางห้องปฏิบัติการ",
                  bold: true,
                  fontSize: 20,
                  alignment: "center",
                  fillColor: COLOR.bannerBg,
                  margin: [0, 3, 0, 0],
                  color: "#FFFFFF",
                },
              ],
            ],
          },
          layout: { hLineWidth: () => 0, vLineWidth: () => 0 },
          marginTop: 4,
          marginBottom: 3,
        },
        {
          table: {
            widths: [82, 190, 118, "*"],
            body: [
              [
                { text: "Name : ", bold: true, fontSize: FS.md, border: NB },
                { text: patient.full_name || "", fontSize: FS.md, border: NB },
                { text: "Lab No. : ", bold: true, fontSize: FS.md, border: NB },
                { text: props.labNo || "", fontSize: FS.md, border: NB },
              ],
              [
                { text: "Age : ", bold: true, fontSize: FS.md, border: NB },
                {
                  text: [
                    { text: `${patient.age || ""} Year` },
                    { text: "     Sex : ", bold: true },
                    { text: gender },
                  ],
                  fontSize: FS.md,
                  border: NB,
                },
                { text: "Collected Date/Time : ", bold: true, fontSize: FS.md, border: NB },
                { text: fmtDatetime(props.collectedDatetime), fontSize: FS.md, border: NB },
              ],
              [
                {
                  text: "ID / Passport No. : ",
                  bold: true,
                  fontSize: FS.md,
                  border: NB,
                  noWrap: true,
                },
                {
                  text: patient.citizen_no || patient.passport_no || "",
                  fontSize: FS.md,
                  border: NB,
                },
                { text: "Received Date/Time : ", bold: true, fontSize: FS.md, border: NB },
                { text: fmtDatetime(props.receivedDatetime), fontSize: FS.md, border: NB },
              ],
              [
                { text: "Patient ID : ", bold: true, fontSize: FS.md, border: NB },
                { text: patient.division_hn || "", fontSize: FS.md, border: NB },
                { text: "Reported Date/Time : ", bold: true, fontSize: FS.md, border: NB },
                { text: fmtDatetime(props.reportedDatetime), fontSize: FS.md, border: NB },
              ],
            ],
          },
          layout: {
            hLineWidth: () => 0,
            vLineWidth: () => 0,
            paddingTop: () => 0,
            paddingBottom: () => 0,
            paddingLeft: () => 0,
            paddingRight: () => 2,
          },
        },
      ],
    }),
    footer,
    content: [
      {
        table: {
          headerRows: 1,
          dontBreakRows: true,
          widths: ["*", 70, 30, 60, 90],
          body: tableBody,
        },
        layout: {
          hLineWidth: () => 1,
          vLineWidth: () => 0,
          hLineColor: () => COLOR.black,
          fillColor: () => null,
          paddingTop: () => 0,
          paddingBottom: () => 1.5,
          paddingLeft: () => 3,
          paddingRight: () => 3,
        },
      },
    ],
  };
};

export default FormMeLabResult;
